import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Shield, Clock, CheckCircle, Zap, Award, FileText, ArrowRight, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { PricingSection } from '../components/PricingSection';
import { NewPricingSection } from '../components/NewPricingSection';
import { TestimonialSlider } from '../components/TestimonialSlider';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '../components/ui/carousel';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { LPFooter } from '../components/LPFooter';
import { faqs, countryCodes } from '../mock';
import { newPricingStructure } from '../pricingData';
import { fetchGeolocation, submitLead, getCountryDialCode, getCountryFlag } from '../services/api';
import { validateEmail, validatePhoneNumber, formatPhoneNumber } from '../utils/validation';
import { toast } from 'sonner';

export const LP = ({ onOpenPopup }) => {
  const navigate = useNavigate();
  const words = ['Brand Name', 'Logo', 'Slogan'];
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Form state
  const [geoData, setGeoData] = useState(null);
  const [isLoadingGeo, setIsLoadingGeo] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    description: '',
    countryDialCode: 1
  });
  
  // Validation errors state
  const [errors, setErrors] = useState({
    email: '',
    phoneNumber: ''
  });
  
  // Fetch geolocation on component mount
  useEffect(() => {
    const loadGeolocation = async () => {
      setIsLoadingGeo(true);
      const result = await fetchGeolocation();
      if (result.success) {
        setGeoData(result); // Store full result including fullResponse
        // Set country dial code from geolocation
        const dialCode = getCountryDialCode(result.data.countryCode);
        setFormData(prev => ({...prev, countryDialCode: dialCode}));
      }
      setIsLoadingGeo(false);
    };
    
    loadGeolocation();
  }, []);
  
  useEffect(() => {
    const currentWord = words[currentWordIndex];
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        if (displayText.length < currentWord.length) {
          setDisplayText(currentWord.slice(0, displayText.length + 1));
        } else {
          setTimeout(() => setIsDeleting(true), 2000);
        }
      } else {
        if (displayText.length > 0) {
          setDisplayText(currentWord.slice(0, displayText.length - 1));
        } else {
          setIsDeleting(false);
          setCurrentWordIndex((prevIndex) => (prevIndex + 1) % words.length);
        }
      }
    }, isDeleting ? 50 : 100);
    
    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, currentWordIndex, words]);
  
  return (
    <div className="pt-20 overflow-x-hidden">
      <Helmet>
        <title>Professional Trademark Registration | Expert Legal Services</title>
        <meta name="description" content="Protect your brand with professional trademark registration. Expert guidance, fast processing, and comprehensive support for your trademark needs." />
      </Helmet>

      {/* Hero Section with Form */}
      <section className="relative bg-gradient-to-br from-orange-600 via-orange-500 to-orange-700 text-white py-12 md:py-16 overflow-hidden">
        {/* Trademark Background Image - Trademark symbols and legal documents */}
        <div className="absolute inset-0 opacity-15 pointer-events-none">
          <img 
            src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1600&h=900&fit=crop"
            alt="Trademark Background"
            className="w-full h-full object-cover"
          />
        </div>
        {/* Animated Particles Background */}
        <div className="absolute inset-0 opacity-20 overflow-hidden">
          <div className="absolute w-full h-full">
            {/* Floating particles effect */}
            <div className="absolute top-[10%] left-[10%] w-2 h-2 bg-white rounded-full animate-float opacity-60"></div>
            <div className="absolute top-[20%] right-[15%] w-3 h-3 bg-yellow-300 rounded-full animate-float opacity-50" style={{ animationDelay: '1s' }}></div>
            <div className="absolute top-[40%] left-[20%] w-2 h-2 bg-white rounded-full animate-float opacity-70" style={{ animationDelay: '2s' }}></div>
            <div className="absolute top-[60%] right-[25%] w-2 h-2 bg-yellow-200 rounded-full animate-float opacity-60" style={{ animationDelay: '1.5s' }}></div>
            <div className="absolute top-[30%] left-[70%] w-3 h-3 bg-white rounded-full animate-float opacity-50" style={{ animationDelay: '0.5s' }}></div>
            <div className="absolute bottom-[20%] left-[15%] w-2 h-2 bg-yellow-300 rounded-full animate-float opacity-70" style={{ animationDelay: '2.5s' }}></div>
            <div className="absolute bottom-[30%] right-[20%] w-2 h-2 bg-white rounded-full animate-float opacity-60" style={{ animationDelay: '3s' }}></div>
          </div>
        </div>
        {/* Gradient mesh animation */}
        <div className="absolute inset-0 opacity-30 overflow-hidden">
          <div className="absolute top-0 left-0 w-96 h-96 bg-orange-400 rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-400 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
          <div className="absolute top-1/2 left-1/3 w-72 h-72 bg-orange-300 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center max-w-7xl mx-auto">
            {/* Mobile: Form First, Desktop: Content First */}
            <div className="pt-4 order-2 lg:order-1">
              <h1 className="text-[40px] md:text-[48px] lg:text-[56px] font-bold mb-6 leading-[1.1] tracking-tight">
                Protect Your <span className="text-white">{displayText}<span className="typing-cursor">|</span></span><br />
                with a <span className="text-white">Registered Trademark</span>
              </h1>
              
              <p className="text-base md:text-lg lg:text-xl mb-6 text-white/90 leading-relaxed font-normal">
                Secure nationwide protection for your brand identity with professional trademark registration and full legal defense under U.S. Federal Law.
              </p>
              
              {/* Key Benefits */}
              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-3 group cursor-pointer">
                  <div className="flex-shrink-0 w-6 h-6 bg-white/20 rounded-full flex items-center justify-center group-hover:bg-yellow-400 transition-all duration-300 group-hover:scale-110">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-base md:text-lg text-white font-normal group-hover:text-yellow-100 transition-colors">USPTO Licensed Attorneys</span>
                </div>
                <div className="flex items-start gap-3 group cursor-pointer">
                  <div className="flex-shrink-0 w-6 h-6 bg-white/20 rounded-full flex items-center justify-center group-hover:bg-yellow-400 transition-all duration-300 group-hover:scale-110">
                    <Zap className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-base md:text-lg text-white font-normal group-hover:text-yellow-100 transition-colors">Fast & Hassle-Free Filing</span>
                </div>
                <div className="flex items-start gap-3 group cursor-pointer">
                  <div className="flex-shrink-0 w-6 h-6 bg-white/20 rounded-full flex items-center justify-center group-hover:bg-yellow-400 transition-all duration-300 group-hover:scale-110">
                    <Award className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-base md:text-lg text-white font-normal group-hover:text-yellow-100 transition-colors">Flat-Fee Pricing — No Hidden Costs</span>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <div className="flex text-yellow-300 text-sm">
                  <span>⭐</span>
                  <span>⭐</span>
                  <span>⭐</span>
                  <span>⭐</span>
                  <span>⭐</span>
                </div>
                <span className="text-xs text-white/90 font-normal">Trusted by 5,000+ entrepreneurs nationwide</span>
              </div>
            </div>
            
            {/* Mobile: Form First (order-1), Desktop: Form Second (order-2) */}
            <div className="relative order-1 lg:order-2">
              {/* Glowing border effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-orange-600 via-yellow-400 to-orange-600 rounded-2xl blur opacity-75 animate-pulse"></div>
              
              {/* Form card */}
              <div className="relative bg-white rounded-xl p-5 shadow-2xl border-2 border-orange-200 max-w-md mx-auto">
                <div className="text-center mb-4">
                  {/* LIMITED TIME OFFER Badge with Lightning Icons */}
                  <div className="inline-flex items-center gap-2 bg-gradient-to-r from-red-600 via-orange-500 to-red-600 text-white text-sm font-black px-6 py-3 rounded-full mb-4 shadow-2xl uppercase tracking-widest">
                    <span className="text-2xl">⚡</span>
                    <span>LIMITED TIME OFFER</span>
                    <span className="text-2xl">⚡</span>
                  </div>
                  
                  {/* Get Started For Just $45 - Single Line, $45 with Blink */}
                  <div className="mb-4">
                    <h2 className="text-2xl md:text-3xl font-black text-gray-900 uppercase tracking-tight">
                      GET STARTED FOR JUST <span className="text-orange-600 animate-pulse">$45</span>
                    </h2>
                  </div>
                  
                  {/* Free Consultation Text */}
                  <p className="text-lg font-bold text-gray-700 mt-6 uppercase tracking-wide">
                    FREE CONSULTATION • NO OBLIGATION
                  </p>
                </div>
                
                {!isLoadingGeo && geoData && geoData.data && (
                  <div className="mb-2 text-center">
                    <p className="text-xs text-gray-600">
                      {getCountryFlag(geoData.data.countryCode)} {geoData.data.countryName}
                    </p>
                  </div>
                )}
                
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  
                  // Validate email
                  const emailValidation = validateEmail(formData.email);
                  if (!emailValidation.isValid) {
                    setErrors(prev => ({ ...prev, email: emailValidation.error }));
                    toast.error(emailValidation.error);
                    return;
                  }
                  
                  // Validate phone number
                  const phoneValidation = validatePhoneNumber(formData.phoneNumber);
                  if (!phoneValidation.isValid) {
                    setErrors(prev => ({ ...prev, phoneNumber: phoneValidation.error }));
                    toast.error(phoneValidation.error);
                    return;
                  }
                  
                  // Clear errors if validation passes
                  setErrors({ email: '', phoneNumber: '' });
                  
                  // Check if geolocation is still loading
                  if (isLoadingGeo) {
                    toast.error('Please wait while we fetch your location...');
                    return;
                  }
                  
                  // Check if geolocation failed to load
                  if (!geoData || !geoData.data) {
                    toast.error('Unable to fetch location. Please refresh the page and try again.');
                    return;
                  }
                  
                  setIsSubmitting(true);
                  
                  try {
                    const result = await submitLead({
                      name: formData.name,
                      email: formData.email,
                      phoneNumber: formData.phoneNumber,
                      description: formData.description,
                      countryDialCode: formData.countryDialCode,
                      country: geoData.data.countryCode || 'US',
                      packageTitle: 'Essentials',
                      packageAmount: 45
                    }, geoData);
                    
                    if (result.success) {
                      console.log('✅ Form submission successful:', result);
                      toast.success(result.message || 'Form submitted successfully!');
                      
                      // Use window.location.href instead of React navigate
                      if (result.redirectUrl) {
                        console.log('🔀 Redirecting to:', result.redirectUrl);
                        window.location.href = result.redirectUrl;
                      } else {
                        console.warn('⚠️ No redirectUrl in result:', result);
                      }
                    } else {
                      console.error('❌ Form submission failed:', result);
                      console.error('Error details:', {
                        error: result.error,
                        statusCode: result.statusCode,
                        fullResult: result
                      });
                      toast.error(result.error || 'Failed to submit form');
                    }
                  } catch (error) {
                    console.error('Form submission exception:', error);
                    toast.error('An error occurred. Please try again.');
                  } finally {
                    setIsSubmitting(false);
                  }
                }} className="space-y-2.5">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Full Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="John Smith"
                      className="w-full h-9 px-3 text-sm rounded-lg border-2 border-gray-200 text-gray-900 placeholder:text-gray-400 hover:border-orange-400 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Email Address *</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData({...formData, email: value});
                        // Clear error on change
                        if (errors.email) {
                          setErrors(prev => ({ ...prev, email: '' }));
                        }
                      }}
                      onBlur={() => {
                        // Validate on blur
                        const validation = validateEmail(formData.email);
                        if (!validation.isValid) {
                          setErrors(prev => ({ ...prev, email: validation.error }));
                        }
                      }}
                      placeholder="john@example.com"
                      className={`w-full h-9 px-3 text-sm rounded-lg border-2 ${
                        errors.email ? 'border-red-500' : 'border-gray-200'
                      } text-gray-900 placeholder:text-gray-400 hover:border-orange-400 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all`}
                    />
                    {errors.email && (
                      <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Phone Number *</label>
                    <div className="flex gap-2">
                      <div className="relative w-32">
                        <Select
                          value={`+${formData.countryDialCode}`}
                          onValueChange={(value) => {
                            const dialCode = parseInt(value.replace('+', ''));
                            setFormData({...formData, countryDialCode: dialCode});
                          }}
                        >
                          <SelectTrigger className="w-full h-9 text-sm font-medium text-gray-900 border-2 border-gray-200 hover:border-orange-400 focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                            <SelectValue>
                              {(() => {
                                const selected = countryCodes.find(c => c.code === `+${formData.countryDialCode}`);
                                if (selected) {
                                  return `${selected.flag} ${selected.code}`;
                                }
                                return '🇺🇸 +1';
                              })()}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent className="max-h-60 z-50">
                            {countryCodes.map((item) => (
                              <SelectItem key={item.country} value={item.code} className="text-xs cursor-pointer">
                                <span className="flex items-center gap-2">
                                  <span>{item.flag}</span>
                                  <span>{item.code}</span>
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <input
                        type="tel"
                        required
                        value={formData.phoneNumber}
                        onChange={(e) => {
                          const formatted = formatPhoneNumber(e.target.value);
                          setFormData({...formData, phoneNumber: formatted});
                          // Clear error on change
                          if (errors.phoneNumber) {
                            setErrors(prev => ({ ...prev, phoneNumber: '' }));
                          }
                        }}
                        onBlur={() => {
                          // Validate on blur
                          const validation = validatePhoneNumber(formData.phoneNumber);
                          if (!validation.isValid) {
                            setErrors(prev => ({ ...prev, phoneNumber: validation.error }));
                          }
                        }}
                        placeholder="Enter phone"
                        maxLength={10}
                        className={`flex-1 h-9 px-3 text-sm rounded-lg border-2 ${
                          errors.phoneNumber ? 'border-red-500' : 'border-gray-200'
                        } text-gray-900 placeholder:text-gray-400 hover:border-orange-400 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all`}
                      />
                    </div>
                    {errors.phoneNumber && (
                      <p className="text-red-500 text-xs mt-1">{errors.phoneNumber}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Description *</label>
                    <textarea
                      required
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      placeholder="Your trademark needs..."
                      rows={2}
                      className="w-full px-3 py-2 text-sm rounded-lg border-2 border-gray-200 text-gray-900 placeholder:text-gray-400 hover:border-orange-400 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none resize-none transition-all"
                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="relative w-full bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 text-white font-bold h-10 text-sm rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 overflow-hidden group disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-yellow-400 to-orange-400 opacity-0 group-hover:opacity-30 transition-opacity duration-300"></span>
                    <span className="relative flex items-center justify-center gap-2">
                      <Shield className="w-4 h-4" />
                      {isSubmitting ? 'Submitting...' : 'Claim Free Consultation'}
                    </span>
                  </button>
                  
                  <p className="text-xs text-center text-gray-500">
                    🔐 Secure and confidential
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Trademark Section */}
      <section className="py-12 md:py-20 bg-white relative overflow-hidden min-h-[600px]">
        {/* Full Section Background Image with people */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1920&h=1080&fit=crop" 
            alt="Professional team working on trademark registration" 
            className="w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/95 via-white/90 to-white/95"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-16 items-center">
              <div className="order-1 lg:order-1">
                <div className="relative">
                  <div className="absolute -top-4 -left-4 w-72 h-72 bg-orange-200 rounded-full blur-3xl opacity-30"></div>
                  <div className="relative rounded-2xl md:rounded-3xl overflow-hidden shadow-xl md:shadow-2xl">
                    <img src="https://images.unsplash.com/photo-1556761175-b413da4baf72?w=800&h=600&fit=crop" alt="Business professionals discussing trademark strategy" className="w-full h-80 md:h-[500px] object-cover"/>
                    <div className="absolute inset-0 bg-gradient-to-t from-orange-900/80 to-transparent flex items-end p-4 md:p-8">
                      <div className="text-white">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="bg-white/20 backdrop-blur-sm p-2 md:p-3 rounded-lg"><Shield size={24} className="md:w-8 md:h-8" /></div>
                          <div><div className="text-lg md:text-2xl font-bold">Brand Protection</div><div className="text-sm md:text-base text-white/90">Legal Security</div></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="order-2 lg:order-2">
                <div className="inline-block bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">📚 Learn About Trademarks</div>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 md:mb-6">What is a Trademark?</h2>
                <div className="space-y-4 text-base text-gray-700 leading-relaxed">
                  <p>A trademark is a unique symbol, word, phrase, design, or combination that identifies and distinguishes your products or services from those of competitors. It's the cornerstone of your brand identity and a valuable business asset.</p>
                  <p>Registering your trademark provides exclusive nationwide rights to use your mark in connection with your goods or services. This legal protection prevents others from using confusingly similar marks and gives you the right to take legal action against infringers.</p>
                  <div className="bg-orange-50 p-6 rounded-2xl border-l-4 border-orange-600">
                    <p className="font-semibold text-gray-900 mb-2">💡 Why Register Your Trademark?</p>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                        <span>Exclusive nationwide rights to use your brand name</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                        <span>Legal presumption of ownership and validity</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                        <span>Ability to bring infringement lawsuits in federal court</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                        <span>Increased brand value and marketability</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3 Step Process Section */}
      <section className="py-12 md:py-20 bg-gradient-to-br from-gray-50 to-orange-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 md:mb-16">
            <div className="inline-block bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">🚀 Simple Process</div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Get Your Trademark in 3 Easy Steps</h2>
            <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto px-4">Our streamlined process makes trademark registration simple and stress-free</p>
          </div>
          
          <div className="max-w-6xl mx-auto">
            {/* Mobile: Carousel */}
            <div className="md:hidden max-w-sm mx-auto mb-8">
              <Carousel>
                <CarouselContent>
                  <CarouselItem>
                    <div className="p-2">
                      <div className="relative bg-white rounded-2xl p-6 shadow-lg border-2 border-orange-100">
                        <div className="absolute -top-3 -right-3 bg-gradient-to-br from-orange-600 to-orange-500 text-white w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold shadow-lg">1</div>
                        <div className="bg-gradient-to-br from-orange-100 to-orange-50 w-14 h-14 rounded-xl flex items-center justify-center mb-4 mx-auto">
                          <FileText className="text-orange-600" size={28} />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Submit Your Information</h3>
                        <p className="text-sm text-gray-600 leading-relaxed mb-3 text-center">
                          Fill out our simple form with your business name, logo, and product/service details.
                        </p>
                        <ul className="space-y-2 text-sm text-gray-700">
                          <li className="flex items-start gap-2">
                            <Check className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                            <span>Quick 5-minute form</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <Check className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                            <span>Free preliminary search</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <Check className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                            <span>Instant confirmation</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </CarouselItem>
                  <CarouselItem>
                    <div className="p-2">
                      <div className="relative bg-white rounded-2xl p-6 shadow-lg border-2 border-orange-100">
                        <div className="absolute -top-3 -right-3 bg-gradient-to-br from-orange-600 to-orange-500 text-white w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold shadow-lg">2</div>
                        <div className="bg-gradient-to-br from-orange-100 to-orange-50 w-14 h-14 rounded-xl flex items-center justify-center mb-4 mx-auto">
                          <Shield className="text-orange-600" size={28} />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Expert Review & Filing</h3>
                        <p className="text-sm text-gray-600 leading-relaxed mb-3 text-center">
                          Our trademark attorneys conduct a comprehensive search and prepare your application.
                        </p>
                        <ul className="space-y-2 text-sm text-gray-700">
                          <li className="flex items-start gap-2">
                            <Check className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                            <span>Comprehensive database search</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <Check className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                            <span>Attorney-prepared application</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <Check className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                            <span>Direct USPTO filing</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </CarouselItem>
                  <CarouselItem>
                    <div className="p-2">
                      <div className="relative bg-white rounded-2xl p-6 shadow-lg border-2 border-orange-100">
                        <div className="absolute -top-3 -right-3 bg-gradient-to-br from-orange-600 to-orange-500 text-white w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold shadow-lg">3</div>
                        <div className="bg-gradient-to-br from-orange-100 to-orange-50 w-14 h-14 rounded-xl flex items-center justify-center mb-4 mx-auto">
                          <Award className="text-orange-600" size={28} />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Monitor & Approve</h3>
                        <p className="text-sm text-gray-600 leading-relaxed mb-3 text-center">
                          We monitor your application throughout the review process and handle any office actions.
                        </p>
                        <ul className="space-y-2 text-sm text-gray-700">
                          <li className="flex items-start gap-2">
                            <Check className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                            <span>Regular status updates</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <Check className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                            <span>Office action responses</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <Check className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                            <span>Certificate delivery</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </CarouselItem>
                </CarouselContent>
                <div className="flex justify-center gap-2 mt-4">
                  <CarouselPrevious className="relative left-0 translate-y-0" />
                  <CarouselNext className="relative right-0 translate-y-0" />
                </div>
              </Carousel>
            </div>
            
            {/* Desktop: Grid */}
            <div className="hidden md:grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Step 1 */}
              <div className="relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 border-2 border-transparent hover:border-orange-200 group cursor-pointer">
                <div className="absolute -top-4 -right-4 bg-gradient-to-br from-orange-600 to-orange-500 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold shadow-lg group-hover:scale-125 group-hover:rotate-12 transition-all duration-500">1</div>
                <div className="bg-gradient-to-br from-orange-100 to-orange-50 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 group-hover:shadow-lg">
                  <FileText className="text-orange-600 group-hover:scale-110 transition-transform duration-300" size={32} />
                </div>
                <h3 className="relative z-10 text-2xl font-bold text-gray-900 mb-4 group-hover:text-orange-600 transition-colors duration-300">Submit Your Information</h3>
                <p className="text-base text-gray-600 leading-relaxed mb-4">
                  Fill out our simple form with your business name, logo, and product/service details. Our system instantly checks for potential conflicts.
                </p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                    <span>Quick 5-minute form</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                    <span>Free preliminary search</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                    <span>Instant confirmation</span>
                  </li>
                </ul>
              </div>

              {/* Step 2 */}
              <div className="relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 border-2 border-transparent hover:border-orange-200 group cursor-pointer">
                <div className="absolute -top-4 -right-4 bg-gradient-to-br from-orange-600 to-orange-500 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold shadow-lg group-hover:scale-125 group-hover:rotate-12 transition-all duration-500">2</div>
                <div className="bg-gradient-to-br from-orange-100 to-orange-50 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 group-hover:shadow-lg">
                  <Shield className="text-orange-600 group-hover:scale-110 transition-transform duration-300" size={32} />
                </div>
                <h3 className="relative z-10 text-2xl font-bold text-gray-900 mb-4 group-hover:text-orange-600 transition-colors duration-300">Expert Review & Filing</h3>
                <p className="text-base text-gray-600 leading-relaxed mb-4">
                  Our trademark attorneys conduct a comprehensive search and prepare your application with precision to maximize approval chances.
                </p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                    <span>Comprehensive database search</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                    <span>Attorney-prepared application</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                    <span>Direct USPTO filing</span>
                  </li>
                </ul>
              </div>

              {/* Step 3 */}
              <div className="relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 border-2 border-transparent hover:border-orange-200 group cursor-pointer">
                <div className="absolute -top-4 -right-4 bg-gradient-to-br from-orange-600 to-orange-500 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold shadow-lg group-hover:scale-125 group-hover:rotate-12 transition-all duration-500">3</div>
                <div className="bg-gradient-to-br from-orange-100 to-orange-50 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 group-hover:shadow-lg">
                  <Award className="text-orange-600 group-hover:scale-110 transition-transform duration-300" size={32} />
                </div>
                <h3 className="relative z-10 text-2xl font-bold text-gray-900 mb-4 group-hover:text-orange-600 transition-colors duration-300">Monitor & Approve</h3>
                <p className="text-base text-gray-600 leading-relaxed mb-4">
                  We monitor your application throughout the review process and handle any office actions. You receive updates every step of the way.
                </p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                    <span>Regular status updates</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                    <span>Office action responses</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                    <span>Certificate delivery</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="text-center mt-12">
              <Button onClick={onOpenPopup} className="bg-orange-600 hover:bg-orange-700 h-12 px-8 text-base hover:scale-105 transition-all inline-flex items-center gap-2">
                Start Your Trademark Registration <ArrowRight className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 md:mb-16">
            <div className="inline-block bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">💰 Transparent Pricing</div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 px-4">Affordable, Flat-Fee Trademark Registration Packages</h2>
            <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto px-4">Handled by Licensed Attorneys</p>
          </div>
          <NewPricingSection onOpenPopup={onOpenPopup} pricingData={newPricingStructure} />
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-12 md:py-20 bg-gradient-to-br from-gray-50 to-orange-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 md:mb-16">
            <div className="inline-block bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">⭐ Why Us</div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Why Choose Our Trademark Services?</h2>
            <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto px-4">Professional guidance and comprehensive support for your legal needs</p>
          </div>
          
          {/* Mobile: Carousel */}
          <div className="md:hidden max-w-sm mx-auto">
            <Carousel>
              <CarouselContent>
                <CarouselItem>
                  <div className="p-2">
                    <div className="bg-white p-6 rounded-2xl shadow-lg border-2 border-orange-100">
                      <div className="bg-gradient-to-br from-orange-100 to-orange-50 w-14 h-14 rounded-xl flex items-center justify-center mb-4 mx-auto">
                        <Shield className="text-orange-600" size={28} />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Expert Legal Team</h3>
                      <p className="text-sm text-gray-600 leading-relaxed text-center">Experienced trademark attorneys review every application, ensuring accuracy and maximizing your chances of approval.</p>
                    </div>
                  </div>
                </CarouselItem>
                <CarouselItem>
                  <div className="p-2">
                    <div className="bg-white p-6 rounded-2xl shadow-lg border-2 border-orange-100">
                      <div className="bg-gradient-to-br from-orange-100 to-orange-50 w-14 h-14 rounded-xl flex items-center justify-center mb-4 mx-auto">
                        <Clock className="text-orange-600" size={28} />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Fast Processing</h3>
                      <p className="text-sm text-gray-600 leading-relaxed text-center">Quick turnaround times with applications filed promptly and efficiently to get your trademark pending status faster.</p>
                    </div>
                  </div>
                </CarouselItem>
                <CarouselItem>
                  <div className="p-2">
                    <div className="bg-white p-6 rounded-2xl shadow-lg border-2 border-orange-100">
                      <div className="bg-gradient-to-br from-orange-100 to-orange-50 w-14 h-14 rounded-xl flex items-center justify-center mb-4 mx-auto">
                        <CheckCircle className="text-orange-600" size={28} />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">High Success Rate</h3>
                      <p className="text-sm text-gray-600 leading-relaxed text-center">Proven track record with thousands of successful trademark registrations across various industries and business types.</p>
                    </div>
                  </div>
                </CarouselItem>
                <CarouselItem>
                  <div className="p-2">
                    <div className="bg-white p-6 rounded-2xl shadow-lg border-2 border-orange-100">
                      <div className="bg-gradient-to-br from-orange-100 to-orange-50 w-14 h-14 rounded-xl flex items-center justify-center mb-4 mx-auto">
                        <Award className="text-orange-600" size={28} />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Comprehensive Support</h3>
                      <p className="text-sm text-gray-600 leading-relaxed text-center">Full-service support from initial search through registration, including office action responses and monitoring.</p>
                    </div>
                  </div>
                </CarouselItem>
                <CarouselItem>
                  <div className="p-2">
                    <div className="bg-white p-6 rounded-2xl shadow-lg border-2 border-orange-100">
                      <div className="bg-gradient-to-br from-orange-100 to-orange-50 w-14 h-14 rounded-xl flex items-center justify-center mb-4 mx-auto">
                        <Zap className="text-orange-600" size={28} />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Transparent Process</h3>
                      <p className="text-sm text-gray-600 leading-relaxed text-center">Clear communication with regular updates on your application status and no hidden fees or surprise charges.</p>
                    </div>
                  </div>
                </CarouselItem>
                <CarouselItem>
                  <div className="p-2">
                    <div className="bg-white p-6 rounded-2xl shadow-lg border-2 border-orange-100">
                      <div className="bg-gradient-to-br from-orange-100 to-orange-50 w-14 h-14 rounded-xl flex items-center justify-center mb-4 mx-auto">
                        <FileText className="text-orange-600" size={28} />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Thorough Research</h3>
                      <p className="text-sm text-gray-600 leading-relaxed text-center">Comprehensive trademark searches to identify potential conflicts before filing, saving you time and money.</p>
                    </div>
                  </div>
                </CarouselItem>
              </CarouselContent>
              <div className="flex justify-center gap-2 mt-4">
                <CarouselPrevious className="relative left-0 translate-y-0" />
                <CarouselNext className="relative right-0 translate-y-0" />
              </div>
            </Carousel>
          </div>
          
          {/* Desktop: Grid */}
          <div className="hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="group relative bg-white p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 border-2 border-orange-100 hover:border-orange-300 transform hover:-translate-y-2 cursor-pointer overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative z-10 bg-gradient-to-br from-orange-100 to-orange-50 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 group-hover:shadow-xl">
                <Shield className="text-orange-600 group-hover:scale-125 transition-transform duration-300" size={32} />
              </div>
              <h3 className="relative z-10 text-2xl font-bold text-gray-900 mb-4 group-hover:text-orange-600 transition-colors duration-300">Expert Legal Team</h3>
              <p className="relative z-10 text-base text-gray-600 leading-relaxed">Experienced trademark attorneys review every application, ensuring accuracy and maximizing your chances of approval.</p>
            </div>
            <div className="group relative bg-white p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 border-2 border-orange-100 hover:border-orange-300 transform hover:-translate-y-2 cursor-pointer overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative z-10 bg-gradient-to-br from-orange-100 to-orange-50 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 group-hover:shadow-xl">
                <Clock className="text-orange-600 group-hover:scale-125 transition-transform duration-300" size={32} />
              </div>
              <h3 className="relative z-10 text-2xl font-bold text-gray-900 mb-4 group-hover:text-orange-600 transition-colors duration-300">Fast Processing</h3>
              <p className="relative z-10 text-base text-gray-600 leading-relaxed">Quick turnaround times with applications filed promptly and efficiently to get your trademark pending status faster.</p>
            </div>
            <div className="group relative bg-white p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 border-2 border-orange-100 hover:border-orange-300 transform hover:-translate-y-2 cursor-pointer overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative z-10 bg-gradient-to-br from-orange-100 to-orange-50 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 group-hover:shadow-xl">
                <CheckCircle className="text-orange-600" size={32} />
              </div>
              <h3 className="relative z-10 text-2xl font-bold text-gray-900 mb-4 group-hover:text-orange-600 transition-colors duration-300">High Success Rate</h3>
              <p className="relative z-10 text-base text-gray-600 leading-relaxed">Proven track record with thousands of successful trademark registrations across various industries and business types.</p>
            </div>
            <div className="group relative bg-white p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 border-2 border-orange-100 hover:border-orange-300 transform hover:-translate-y-2 cursor-pointer overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative z-10 bg-gradient-to-br from-orange-100 to-orange-50 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 group-hover:shadow-xl">
                <Award className="text-orange-600" size={32} />
              </div>
              <h3 className="relative z-10 text-2xl font-bold text-gray-900 mb-4 group-hover:text-orange-600 transition-colors duration-300">Comprehensive Support</h3>
              <p className="relative z-10 text-base text-gray-600 leading-relaxed">Full-service support from initial search through registration, including office action responses and monitoring.</p>
            </div>
            <div className="group relative bg-white p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 border-2 border-orange-100 hover:border-orange-300 transform hover:-translate-y-2 cursor-pointer overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative z-10 bg-gradient-to-br from-orange-100 to-orange-50 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 group-hover:shadow-xl">
                <Zap className="text-orange-600" size={32} />
              </div>
              <h3 className="relative z-10 text-2xl font-bold text-gray-900 mb-4 group-hover:text-orange-600 transition-colors duration-300">Transparent Process</h3>
              <p className="relative z-10 text-base text-gray-600 leading-relaxed">Clear communication with regular updates on your application status and no hidden fees or surprise charges.</p>
            </div>
            <div className="group relative bg-white p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 border-2 border-orange-100 hover:border-orange-300 transform hover:-translate-y-2 cursor-pointer overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative z-10 bg-gradient-to-br from-orange-100 to-orange-50 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 group-hover:shadow-xl">
                <FileText className="text-orange-600" size={32} />
              </div>
              <h3 className="relative z-10 text-2xl font-bold text-gray-900 mb-4 group-hover:text-orange-600 transition-colors duration-300">Thorough Research</h3>
              <p className="relative z-10 text-base text-gray-600 leading-relaxed">Comprehensive trademark searches to identify potential conflicts before filing, saving you time and money.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 md:mb-16">
            <div className="inline-block bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">💬 Client Reviews</div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 px-4">What Our Clients Say</h2>
            <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto px-4">Trusted by businesses nationwide</p>
          </div>
          <TestimonialSlider hideHeader={true} />
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-12 md:py-20 bg-gradient-to-br from-gray-50 to-orange-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 md:mb-16">
            <div className="inline-block bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">❓ Got Questions?</div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Everything you need to know about trademark registration</p>
          </div>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.slice(0, 8).map((faq) => (
                <AccordionItem key={faq.id} value={`item-${faq.id}`} className="bg-white rounded-2xl px-6 border-none shadow-sm hover:shadow-md transition-shadow">
                  <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-orange-600 py-6">{faq.question}</AccordionTrigger>
                  <AccordionContent className="text-base text-gray-600 pb-6 leading-relaxed">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-20 bg-gradient-to-br from-orange-600 via-orange-500 to-orange-700 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-96 h-96 bg-orange-400 rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-orange-400 rounded-full blur-3xl animate-pulse-slow"></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Protect Your Brand?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-white/90">Join thousands of satisfied clients who trust our professional trademark services</p>
          <Button onClick={onOpenPopup} className="bg-white text-orange-600 hover:bg-gray-100 h-14 px-10 text-lg hover:scale-105 transition-all shadow-xl inline-flex items-center gap-2">
            Start Your Application Today <ArrowRight className="w-6 h-6" />
          </Button>
        </div>
      </section>

      {/* LP Footer */}
      <LPFooter />
    </div>
  );
};
